import React from 'react';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

import { useFormField } from './FormField';

interface FormLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement>, ForwardedHTMLProps {
  children: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  hint?: string;
  size?: ComponentSize;
  srOnly?: boolean;
  className?: string;
}

const sizeClasses: Record<ComponentSize, string> = {
  xs: 'text-xs',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
};

export const FormLabel = React.forwardRef<HTMLLabelElement, FormLabelProps>(
  (
    {
      children,
      required: requiredProp,
      optional = false,
      hint,
      size: sizeProp,
      srOnly = false,
      className,
      htmlFor,
      ...props
    },
    ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);
    const fieldContext = useFormField();

    const required = requiredProp ?? fieldContext?.required ?? false;
    const size = sizeProp ?? fieldContext?.size ?? 'md';
    const labelFor = htmlFor ?? fieldContext?.id;
    const isDisabled = fieldContext?.disabled ?? false;

    if (srOnly) {
      return (
        <label ref={ref} htmlFor={labelFor} className="sr-only" {...forwardedProps} {...props}>
          {children}
          {required && <span> (required)</span>}
        </label>
      );
    }

    return (
      <label
        ref={ref}
        htmlFor={labelFor}
        className={cn(
          'flex items-center gap-1',
          sizeClasses[size],
          'font-medium',
          tef.colors.foreground,
          isDisabled && 'opacity-60',
          className
        )}
        {...forwardedProps}
        {...props}
      >
        <span>{children}</span>
        {required && (
          <span className="text-red-500 dark:text-red-400" aria-hidden="true">
            *
          </span>
        )}
        {optional && !required && <span className={cn('font-normal', tef.colors.foregroundTertiary)}>(optional)</span>}
        {hint && <span className={cn('font-normal ml-1', tef.colors.foregroundTertiary)}>— {hint}</span>}
      </label>
    );
  }
);

FormLabel.displayName = 'FormLabel';

export default FormLabel;
